import * as React from "react";
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import Fade from "@mui/material/Fade";
import Slide from "@mui/material/Slide";
import Zoom from "@mui/material/Zoom";
import { styled, useTheme, useColorScheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import AppTheme from "../shared-theme/AppTheme";
import axiosInstance from "../config/axiosInterceptor";
import KEYCLOAK_CONFIG from "../config/keycloakConfig";
import { getCurrentTenant } from "../config/tenantConfig";
import { useTranslation } from "react-i18next";
import "../i18n/i18n";

const { apiBaseUrl: API_BASE_URL } = getCurrentTenant();

// Glassmorphism Efektli Kart (SignIn ile aynı)
const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(5),
  gap: theme.spacing(3),
  margin: "auto",
  maxWidth: "520px",
  borderRadius: "24px",
  backdropFilter: "blur(20px)",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(30, 30, 40, 0.7)"
      : "rgba(255, 255, 255, 0.8)",
  border:
    theme.palette.mode === "dark"
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(255, 255, 255, 0.5)",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
      : "0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
  animation: "fadeInUp 0.8s ease-out",
  "@keyframes fadeInUp": {
    "0%": {
      opacity: 0,
      transform: "translateY(20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

// Açık Yeşil ve Beyaz Gradyan Arka Plan (SignIn ile aynı)
const ConfirmRoleContainer = styled(Stack)(({ theme }) => ({
  height: "100dvh",
  minHeight: "100%",
  padding: theme.spacing(2),
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  background:
    theme.palette.mode === "dark"
      ? "radial-gradient(at 50% 0%, hsla(150, 20%, 20%, 1) 0, transparent 70%), radial-gradient(at 50% 100%, hsla(150, 25%, 10%, 1) 0, transparent 70%)"
      : `
      radial-gradient(at 50% 50%, #ffffff 0%, transparent 60%),
      radial-gradient(at 0% 0%, hsla(145, 60%, 96%, 1) 0, transparent 50%),
      radial-gradient(at 100% 100%, hsla(145, 60%, 94%, 1) 0, transparent 50%)
    `,
  backgroundColor: theme.palette.mode === "dark" ? "#0d1f0d" : "#f0fdf4",
}));

// Info Item Component with hover transition
const InfoItem = ({ label, value, isCode = false, theme, delay = 0 }) => (
  <Fade in={true} timeout={600} style={{ transitionDelay: `${delay}ms` }}>
    <Box
      sx={{
        mb: 2,
        transition: "transform 0.2s ease, background-color 0.2s ease",
        borderRadius: "8px",
        p: 1,
        mx: -1,
        textAlign: "center",
        "&:hover": {
          transform: "scale(1.02)",
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.03)"
              : "rgba(0, 0, 0, 0.02)",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: "0.75rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          display: "block",
          mb: 0.5,
          transition: "color 0.2s ease",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "1.1rem",
          fontWeight: 500,
          color: theme.palette.text.primary,
          transition: "color 0.2s ease",
        }}
      >
        {value}
      </Typography>
    </Box>
  </Fade>
);

// Force light mode on this page
function SystemModeSync() {
  const { setMode } = useColorScheme();
  useEffect(() => {
    setMode("light");
  }, [setMode]);
  return null;
}

export default function ConfirmRole(props) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [token, setToken] = useState(null);
  const [tokenChecked, setTokenChecked] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();

  // Check token on mount — if an active client session exists, do full Keycloak logout
  useEffect(() => {
    const storedToken = localStorage.getItem("authToken");
    const companyName = localStorage.getItem("companyName");
    console.log("[ConfirmRole] Token check:", !!storedToken, "companyName:", companyName);

    // Active client session = companyName is set and is not DefaultClient
    if (storedToken && companyName && companyName !== "DefaultClient") {
      console.log("[ConfirmRole] Active client session detected (companyName:", companyName, "). Performing full Keycloak logout...");
      setRedirecting(true);

      // 1. Save confirmRole params before clearing everything
      const confirmRoleData = {
        projectCode: searchParams.get("projectCode"),
        adminName: searchParams.get("adminName"),
        role: searchParams.get("role"),
        expireTime: searchParams.get("expireTime"),
        securityCode: searchParams.get("code"),
      };

      // 2. Get idToken for Keycloak logout
      const idToken = localStorage.getItem("idToken");

      // 3. Clear all auth data
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("idToken");
      localStorage.removeItem("userName");
      localStorage.removeItem("adminId");
      localStorage.removeItem("userRoles");
      localStorage.removeItem("objectData");
      localStorage.removeItem("enumData");
      localStorage.removeItem("projectCode");
      localStorage.removeItem(companyName);
      localStorage.removeItem("companyName");

      // 4. Save pendingConfirmRole AFTER clearing (so it's not removed)
      localStorage.setItem("pendingConfirmRole", JSON.stringify(confirmRoleData));
      console.log("[ConfirmRole] Saved pendingConfirmRole, redirecting to Keycloak logout...");

      // 5. Full Keycloak logout to end SSO session
      const postLogoutRedirectUri = `${window.location.origin}/login`;
      let logoutUrl = `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/logout?post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}&client_id=${KEYCLOAK_CONFIG.clientId}`;
      if (idToken) {
        logoutUrl += `&id_token_hint=${idToken}`;
      }

      window.location.href = logoutUrl;
      return;
    }

    setToken(storedToken);
    setTokenChecked(true);
  }, []);

  const projectCode = searchParams.get("projectCode");
  const adminName = searchParams.get("adminName") || "-";
  const role = searchParams.get("role") || "-";
  const expireTime = searchParams.get("expireTime");
  const securityCode = searchParams.get("code") || "-";

  const expireDate = expireTime
    ? new Date(parseInt(expireTime) * 1000).toLocaleString("tr-TR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

  // If no token, save params and redirect to Keycloak login
  useEffect(() => {
    // Wait until token check is complete
    if (!tokenChecked) {
      console.log("[ConfirmRole] Waiting for token check...");
      return;
    }

    console.log("[ConfirmRole] Token checked, token exists:", !!token);

    if (!token) {
      setRedirecting(true);

      // Save confirmRole params to localStorage before redirecting
      const confirmRoleData = {
        projectCode: searchParams.get("projectCode"),
        adminName: searchParams.get("adminName"),
        role: searchParams.get("role"),
        expireTime: searchParams.get("expireTime"),
        securityCode: searchParams.get("code"),
      };
      localStorage.setItem(
        "pendingConfirmRole",
        JSON.stringify(confirmRoleData),
      );
      console.log(
        "[ConfirmRole] Saved pendingConfirmRole, redirecting to Keycloak...",
      );

      // Redirect to login page (which handles Keycloak callback)
      // After login, user will be redirected back to confirmRole
      const realmUrl = KEYCLOAK_CONFIG.realmUrl;
      const uiLocale = localStorage.getItem("i18nextLng") || "tr-TR";
      const redirectUri = `${window.location.origin}/login`;

      const authUrl = `${realmUrl}/protocol/openid-connect/auth?client_id=${KEYCLOAK_CONFIG.clientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&scope=openid&ui_locales=${uiLocale}`;

      window.location.href = authUrl;
    }
  }, [token, tokenChecked, projectCode, searchParams]);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Test için localhost kullanılıyor, production'da axiosInstance kullanılacak
      const response = await fetch(
        `${API_BASE_URL}/${projectCode}/api/WebUser/ConfirmRole`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            projectName: projectCode,
            adminName,
            role,
            expireTime: parseInt(expireTime, 10),
            code: securityCode,
          }),
        },
      );
      console.log("[ConfirmRole] ConfirmRole API response status:", response.status);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("[ConfirmRole] ConfirmRole API FAILED:", errorData);
        throw new Error(errorData.message || "İstek başarısız oldu");
      }
      console.log("[ConfirmRole] ConfirmRole API SUCCESS");

      // DefaultClient ile fresh token al (yeni client resource_access'te görünsün)
      console.log("[ConfirmRole] === POST-CONFIRM TOKEN REFRESH (DefaultClient) ===");
      const refreshToken = localStorage.getItem("refreshToken");
      console.log("[ConfirmRole] refreshToken exists:", !!refreshToken);

      if (refreshToken) {
        // Önce DefaultClient context'inde refresh yap
        console.log("[ConfirmRole] Refreshing with client_id:", KEYCLOAK_CONFIG.clientId, "(DefaultClient)");

        const refreshParams = new URLSearchParams({
          grant_type: "refresh_token",
          client_id: KEYCLOAK_CONFIG.clientId,
          refresh_token: refreshToken,
        });

        const refreshRes = await fetch(
          `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
          {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: refreshParams,
          }
        );

        console.log("[ConfirmRole] Refresh response status:", refreshRes.status);
        const refreshData = await refreshRes.json();
        if (refreshData.access_token) {
          console.log("[ConfirmRole] Token refresh SUCCESS - new client should be in resource_access");
          localStorage.setItem("authToken", refreshData.access_token);
          if (refreshData.refresh_token) {
            localStorage.setItem("refreshToken", refreshData.refresh_token);
          }
          // Client scope'u temizle - /client sayfası yeniden seçim yapsın
          localStorage.removeItem("companyName");
        } else {
          console.error("[ConfirmRole] Token refresh FAILED:", refreshData.error, refreshData.error_description);
        }
      } else {
        console.warn("[ConfirmRole] No refresh token found, skipping token refresh");
      }
      console.log("[ConfirmRole] === POST-CONFIRM TOKEN REFRESH COMPLETE ===");

      setConfirmed(true);
      // Redirect to /client after showing success
      setTimeout(() => {
        navigate("/client");
      }, 2000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          t("confirmRole.error") ||
          "Rol onayı sırasında bir hata oluştu.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking token or redirecting to Keycloak
  if (!tokenChecked || !token || redirecting) {
    return (
      <AppTheme {...props}>
        <SystemModeSync />
        <CssBaseline enableColorScheme />
        <ConfirmRoleContainer
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Card variant="outlined">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress size={48} sx={{ color: "primary.main" }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                }}
              >
                {t("confirmRole.redirecting") ||
                  "Giriş sayfasına yönlendiriliyorsunuz..."}
              </Typography>
            </Box>
          </Card>
        </ConfirmRoleContainer>
      </AppTheme>
    );
  }

  return (
    <AppTheme {...props}>
      <SystemModeSync />
      <CssBaseline enableColorScheme />
      <ConfirmRoleContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          {confirmed ? (
            /* Success State with Animations */
            <Fade in={confirmed} timeout={500}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 3,
                }}
              >
                <Zoom
                  in={confirmed}
                  timeout={600}
                  style={{ transitionDelay: "100ms" }}
                >
                  <CheckCircleOutlineIcon
                    sx={{
                      fontSize: 80,
                      color: "#4CAF50",
                      mb: 2,
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%": { transform: "scale(1)" },
                        "50%": { transform: "scale(1.05)" },
                        "100%": { transform: "scale(1)" },
                      },
                    }}
                  />
                </Zoom>
                <Slide
                  direction="up"
                  in={confirmed}
                  timeout={500}
                  style={{ transitionDelay: "200ms" }}
                >
                  <Typography
                    variant="h4"
                    fontWeight="700"
                    sx={{
                      textAlign: "center",
                      background:
                        "linear-gradient(45deg, #4CAF50 30%, #81C784 90%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      mb: 1,
                    }}
                  >
                    {t("confirmRole.success") || "Rol Onaylandı"}
                  </Typography>
                </Slide>
                <Fade
                  in={confirmed}
                  timeout={600}
                  style={{ transitionDelay: "400ms" }}
                >
                  <Typography
                    sx={{
                      color: theme.palette.text.secondary,
                      textAlign: "center",
                    }}
                  >
                    {t("confirmRole.successDescription") ||
                      "Yetki tanımlaması başarıyla onaylandı."}
                  </Typography>
                </Fade>
                <Fade
                  in={confirmed}
                  timeout={600}
                  style={{ transitionDelay: "600ms" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2, gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} />
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {t("clientSelect.redirecting") || "Client seçimine yönlendiriliyorsunuz..."}
                    </Typography>
                  </Box>
                </Fade>
              </Box>
            </Fade>
          ) : (
            <>
              {/* Header with Icon and Animations */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Zoom in={true} timeout={600}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <VerifiedUserIcon
                      sx={{
                        fontSize: 56,
                        color: "#2196F3",
                        transition: "transform 0.3s ease, color 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.1) rotate(5deg)",
                          color: "#21CBF3",
                        },
                      }}
                    />
                  </Box>
                </Zoom>
                <Fade
                  in={true}
                  timeout={600}
                  style={{ transitionDelay: "200ms" }}
                >
                  <Typography
                    variant="h4"
                    fontWeight="700"
                    sx={{
                      textAlign: "center",
                      background:
                        "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      mb: 1,
                      transition: "transform 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.02)",
                      },
                    }}
                  >
                    {t("confirmRole.title") || "Erişim Onayı"}
                  </Typography>
                </Fade>
                <Fade
                  in={true}
                  timeout={600}
                  style={{ transitionDelay: "400ms" }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      textAlign: "center",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {t("confirmRole.description") ||
                      "Aşağıdaki yetki tanımlamasını doğrulamak üzeresiniz."}
                  </Typography>
                </Fade>
              </Box>

              {/* Info Grid with staggered animations */}
              <Slide
                direction="up"
                in={true}
                timeout={500}
                style={{ transitionDelay: "300ms" }}
              >
                <Box
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 0, 0, 0.2)"
                        : "rgba(0, 0, 0, 0.02)",
                    borderRadius: "16px",
                    p: 3,
                    mb: 2,
                    transition: "box-shadow 0.3s ease, transform 0.3s ease",
                    "&:hover": {
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 4px 20px rgba(0, 0, 0, 0.3)"
                          : "0 4px 20px rgba(0, 0, 0, 0.08)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <InfoItem
                    label={t("confirmRole.adminName") || "Sorumlu Yönetici"}
                    value={adminName}
                    theme={theme}
                    delay={100}
                  />
                  <InfoItem
                    label={t("confirmRole.role") || "Tanımlanan Rol"}
                    value={role}
                    theme={theme}
                    delay={200}
                  />
                  <InfoItem
                    label={t("confirmRole.expireTime") || "Son Geçerlilik"}
                    value={expireDate}
                    theme={theme}
                    delay={300}
                  />
                </Box>
              </Slide>

              {/* Error Message with shake animation */}
              <Fade in={!!error} timeout={300}>
                <Box
                  sx={{
                    backgroundColor: "rgba(244, 67, 54, 0.1)",
                    border: "1px solid rgba(244, 67, 54, 0.3)",
                    borderRadius: "12px",
                    p: 2,
                    mb: 2,
                    display: error ? "block" : "none",
                    animation: error ? "shake 0.5s ease-in-out" : "none",
                    "@keyframes shake": {
                      "0%, 100%": { transform: "translateX(0)" },
                      "10%, 30%, 50%, 70%, 90%": {
                        transform: "translateX(-4px)",
                      },
                      "20%, 40%, 60%, 80%": { transform: "translateX(4px)" },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: "#f44336",
                      fontSize: "0.875rem",
                      textAlign: "center",
                    }}
                  >
                    {error}
                  </Typography>
                </Box>
              </Fade>

              {/* Confirm Button with hover effects */}
              <Fade
                in={true}
                timeout={600}
                style={{ transitionDelay: "500ms" }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleConfirm}
                  disabled={loading}
                  startIcon={!loading && <CheckCircleOutlineIcon />}
                  sx={{
                    py: 1.8,
                    fontSize: "1rem",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "12px",
                    background:
                      "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                    boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                      transition: "left 0.5s ease",
                    },
                    "&:hover": {
                      background:
                        "linear-gradient(45deg, #1976D2 30%, #00BCD4 90%)",
                      transform: "scale(1.02) translateY(-2px)",
                      boxShadow: "0 8px 20px 4px rgba(33, 203, 243, .4)",
                      "&::before": {
                        left: "100%",
                      },
                    },
                    "&:active": {
                      transform: "scale(0.98)",
                    },
                    "&:disabled": {
                      background:
                        "linear-gradient(45deg, #90CAF9 30%, #B2EBF2 90%)",
                      transform: "none",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("confirmRole.confirmButton") || "Rolü Onayla"
                  )}
                </Button>
              </Fade>

              {/* Footer Note */}
              <Fade
                in={true}
                timeout={600}
                style={{ transitionDelay: "600ms" }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    color: theme.palette.text.secondary,
                    mt: 2,
                    transition: "opacity 0.3s ease",
                    "&:hover": {
                      opacity: 0.8,
                    },
                  }}
                >
                  {t("confirmRole.footerNote") ||
                    "Bu işlem Keycloak üzerinde loglanacaktır."}
                </Typography>
              </Fade>
            </>
          )}
        </Card>
      </ConfirmRoleContainer>
    </AppTheme>
  );
}
